import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useServerFreshness } from '../lib/useServerFreshness';
import { useState, useEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import DemoDataBanner from './DemoDataBanner';
import ErrorBoundary from './ErrorBoundary';
import OnboardingTour from './OnboardingTour';
import GuidedTour from './GuidedTour';
import PersonaPrompt from './PersonaPrompt';
import NoDataPrompt from './NoDataPrompt';
import DocImportModal from './import/DocImportModal';
import SessionIdleModal from './SessionIdleModal';
import PrivacyAcceptanceGate from './legal/PrivacyAcceptanceGate';
import CommandCenterEasterEgg from './CommandCenterEasterEgg';
import RouteSuspense from './shared/RouteSuspense';
import { startSessionManager, stopSessionManager } from '../lib/sessionManager';
import { tourAutoFireDecision } from '../lib/tourAutoFire';
import { PlanContext, usePlanState } from '../lib/usePlan';
import { useAuthStore } from '../store/useAuthStore';
import { useFilterStore } from '../store/useFilterStore';
import { useTourStore } from '../store/useTourStore';
import { ticketsApi, authApi, orgApi, tourStepsApi, demoDataApi } from '../lib/api';
import type { TourStep } from '../types';

const NO_DATA_DISMISS_KEY = 'avium-no-data-dismissed';

const isStaging = typeof window !== 'undefined' && window.location.hostname.includes('staging');

export default function Layout() {
  useServerFreshness(); // server-side syncs (doorbell/scheduled) refresh open surfaces
  const planState = usePlanState();
  const location = useLocation();
  const navigate = useNavigate();

  // Interim mobile strategy (2026-06-06): render the FULL DESKTOP app on phones
  // (zoomed out + pannable) instead of a half-built mobile layout. While the
  // authenticated shell is mounted we pin a fixed desktop-width viewport, and
  // restore the responsive viewport on unmount so the PUBLIC pages (/founders,
  // /blog, /login, marketing) stay mobile-friendly. A true responsive redesign
  // is a deferred backlog project; this is the stopgap so phone users see the
  // real product rather than the untested /today surface.
  useEffect(() => {
    const vp = document.querySelector('meta[name="viewport"]');
    const prev = vp?.getAttribute('content') ?? 'width=device-width, initial-scale=1.0';
    vp?.setAttribute('content', 'width=1280');
    return () => { vp?.setAttribute('content', prev); };
  }, []);

  // Session idle-timeout enforcement (lib/sessionManager.ts): runs for the
  // whole authenticated shell — tracks activity, silently renews the access
  // token while the user is active, and drives SessionIdleModal's countdown.
  useEffect(() => {
    startSessionManager();
    return () => stopSessionManager();
  }, []);

  // Marketing carousel ("Product overview") — NO LONGER auto-fires on first
  // login (re-sequenced 2026-05-31, Option A). It's now re-openable collateral
  // from the sidebar; the guided tour below is the first-run experience. Kept
  // mounted at Layout level so it overlays every route in the authenticated
  // shell when opened.
  const user = useAuthStore(s => s.user);
  const updateUser = useAuthStore(s => s.updateUser);
  const [tourOpen, setTourOpen] = useState(false);
  // Sidebar dispatches this event when the user clicks "Product overview".
  // The carousel is collateral now — opening it never marks the welcome flag
  // and never hands off to the guided tour (markSeenOnClose stays false).
  useEffect(() => {
    const onOpenTour = () => setTourOpen(true);
    window.addEventListener('avium:open-tour', onOpenTour);
    return () => window.removeEventListener('avium:open-tour', onOpenTour);
  }, []);

  // ── Guided product tour — the first-run orientation experience ────────────
  // Interactive walkthrough of the real surfaces. Auto-fires for first-time
  // users (tourCompletedAt == null) on the /brief landing (moved from
  // /pipeline 2026-07-20 alongside the home-base change) — it no longer
  // waits for the carousel. Persistence is server-side (tourCompletedAt) so
  // the tour doesn't re-show across devices.
  const [guidedTourOpen, setGuidedTourOpen] = useState(false);
  // Mirror to the shared store so other components (IntelligenceGate) can
  // suppress their own modal overlays while the tour is running. User
  // report 2026-05-24: tour tooltip + gate ack modal stacking on top of
  // each other on Dashboard / Intelligence steps.
  const setGuidedOpenInStore = useTourStore(s => s.setGuidedOpen);
  useEffect(() => {
    setGuidedOpenInStore(guidedTourOpen);
  }, [guidedTourOpen, setGuidedOpenInStore]);
  // Increments on every open. Used as the GuidedTour key so the component
  // fully remounts each run — guarantees idx, rect, and any other internal
  // state reset to defaults instead of leaking from the prior session
  // (the source of the "Restart shows no highlight" bug).
  const [guidedTourSession, setGuidedTourSession] = useState(0);
  // Demo-data orchestration around the tour. The Intelligence/Capacity/Reports
  // surfaces are tier-gated AND data-empty for new workspaces, so the tour
  // walks the user through screens that say "nothing here yet". To fix that
  // we auto-load the Dunder Mifflin demo project before opening the tour
  // (only if no demo data is currently present) and tear it down again on
  // close. Real data is never touched.
  const [tourSeeding, setTourSeeding] = useState(false);
  const [tourSeedError, setTourSeedError] = useState<string | null>(null);
  // True only if THIS tour run is the one that seeded the demo — controls
  // whether we delete on close. Pre-existing demo data is left alone.
  const weSeededForTourRef = useRef(false);
  const seedPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clearSeedPoll = () => {
    if (seedPollRef.current) { clearInterval(seedPollRef.current); seedPollRef.current = null; }
  };

  const openTourImmediate = () => {
    setGuidedTourSession(s => s + 1);
    setGuidedTourOpen(true);
  };

  const openGuidedTour = async () => {
    // Decide whether to seed demo data for the tour. The server's /status
    // endpoint reports two booleans (both best-effort):
    //   • hasRealData — the org has its OWN tickets (imported from Jira/Excel
    //     or created in Avium). If true we must NEVER seed Dunder Mifflin on
    //     top of a real workspace (would pollute the user's data). Run the
    //     tour on their data instead.
    //   • present — demo data already exists (ticket-validated server-side via
    //     hasDemoData, so it's not the stale-flag case the old client-side
    //     secondary check guarded against). Run on it without claiming
    //     ownership — the user manages it via the DemoDataBanner.
    // Only a genuinely empty workspace gets a fresh tour-owned seed.
    let demoPresent = false;
    let realData = false;
    try {
      const status = await demoDataApi.status();
      demoPresent = !!status.data?.present;
      realData = !!status.data?.hasRealData;
    } catch {
      // Status API failed — leave both false so we attempt to seed.
    }

    if (realData) {
      // The user has their own data — suppress the demo seed entirely and run
      // the tour against their real workspace.
      weSeededForTourRef.current = false;
      openTourImmediate();
      return;
    }

    if (demoPresent) {
      // Demo data already loaded (and no real data) — don't claim ownership;
      // tour opens immediately on it.
      weSeededForTourRef.current = false;
      openTourImmediate();
      return;
    }

    // Empty workspace — seed fresh data for the tour and claim ownership so
    // cleanupTourDemo deletes it on tour end.
    setTourSeeding(true);
    setTourSeedError(null);
    try {
      await demoDataApi.seed();
    } catch {
      setTourSeedError('Could not start sample data load.');
      return;
    }
    const deadline = Date.now() + 75_000;
    clearSeedPoll();
    seedPollRef.current = setInterval(async () => {
      if (Date.now() > deadline) {
        clearSeedPoll();
        setTourSeedError('Sample data is taking longer than expected.');
        return;
      }
      try {
        const r = await demoDataApi.status();
        if (r.data?.error) {
          clearSeedPoll();
          setTourSeedError(r.data.error);
          return;
        }
        if (r.data?.present) {
          clearSeedPoll();
          weSeededForTourRef.current = true;
          setTourSeeding(false);
          // Tell already-mounted pages (PipelinePage, DashboardPage, the
          // Intelligence views, etc.) that fresh data is now available
          // server-side so they refetch BEFORE the tour highlights them.
          // Without this, the tour opens step 1 on /pipeline with the
          // empty-board state PipelinePage captured at mount, defeating
          // the whole point of auto-seeding. Symmetric to the
          // 'demo-data-deleted' event the cleanupTourDemo dispatches.
          // User report 2026-05-24: "the guided tour really has problems
          // on the first page see the work. There is no work."
          window.dispatchEvent(new CustomEvent('avium:demo-data-loaded'));
          openTourImmediate();
        }
      } catch { /* transient — keep polling until the deadline */ }
    }, 2500);
  };

  const cleanupTourDemo = async () => {
    if (!weSeededForTourRef.current) return;
    weSeededForTourRef.current = false;
    try {
      await demoDataApi.delete();
      useFilterStore.getState().clearFilters();
      // Pass `fromTour:true` so the demo-data-deleted listener (which is
      // shared with the user-driven DemoDataBanner delete) skips the
      // welcomeSeen / tourCompletedAt reset. The user just FINISHED the
      // tour — re-firing the welcome carousel on them is the wrong UX,
      // and the chain of state resets was the root cause of the
      // "white screen on tour finish" bug (user report 2026-05-24).
      window.dispatchEvent(new CustomEvent('demo-data-deleted', {
        detail: { fromTour: true },
      }));
    } catch (err) {
      // Best-effort: user can delete manually via the banner. Log so we
      // catch silent regressions in CI / production telemetry.
      console.error('[Layout] cleanupTourDemo failed', err);
    }
  };

  // Belt-and-suspenders: if the tab closes mid-tour, kill the demo. Best-
  // effort only (browsers limit what unload handlers can do), but better
  // than orphaning a Dunder Mifflin project in the user's workspace.
  useEffect(() => {
    const onUnload = () => {
      if (weSeededForTourRef.current) {
        try { void demoDataApi.delete(); } catch { /* nothing we can do */ }
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);
  const [companyLogo, setCompanyLogo] = useState<string>('');
  useEffect(() => {
    orgApi.branding().then(r => {
      const logo = r.data.find((c: { key: string; value: string }) => c.key === 'logo-url');
      setCompanyLogo(logo?.value || '');
    }).catch(() => {});
  }, []);
  // Tour content lives in the DB (per-org, admin-editable). Fetched once on
  // mount; if the org has none, the server auto-seeds the canonical defaults
  // so first-load works without any admin action.
  const [tourSteps, setTourSteps] = useState<TourStep[]>([]);
  useEffect(() => {
    if (!user) return;
    tourStepsApi.list()
      .then(r => setTourSteps(r.data))
      .catch(() => setTourSteps([])); // silent — guided tour just won't fire
  }, [user]);
  // Translate DB rows into the prop shape GuidedTour expects. Filter out
  // anything missing a title/body so a half-edited row can't break the tour.
  const guidedSteps = tourSteps
    .filter(s => s.title && s.body)
    .map(s => ({
      target: s.target ?? null,
      placement: (s.placement ?? 'bottom') as 'bottom' | 'top' | 'right' | 'left',
      route: s.route ?? null,
      title: s.title,
      body: s.body,
    }));
  // Auto-fire on the Brief LANDING only — never on an in-app drill arrival
  // (?keys=… from a signal, or any other query string), and at most once per
  // app load; the gate itself is pure + tested in lib/tourAutoFire.ts. Once
  // running, the tour drives its own navigation via per-step routes.
  const tourAutoFireSpentRef = useRef(false);
  useEffect(() => {
    if (!user) return;
    const decision = tourAutoFireDecision({
      tourCompletedAt: user.tourCompletedAt,
      carouselOpen: tourOpen,
      stepsLoaded: guidedSteps.length > 0,
      pathname: location.pathname,
      search: location.search,
      spent: tourAutoFireSpentRef.current,
    });
    if (decision.spend) tourAutoFireSpentRef.current = true;
    if (decision.fire) openGuidedTour();
  }, [user, tourOpen, location.pathname, location.search, guidedSteps.length]);
  // Sidebar entry to re-run the guided tour later (mirrors "Take the tour").
  useEffect(() => {
    const onOpen = () => openGuidedTour();
    window.addEventListener('avium:open-guided-tour', onOpen);
    return () => window.removeEventListener('avium:open-guided-tour', onOpen);
  }, []);
  const handleGuidedComplete = () => {
    setGuidedTourOpen(false);
    updateUser({ tourCompletedAt: new Date().toISOString() });
    authApi.markTourComplete().catch(() => { /* best-effort; local state is enough for this session */ });
    void cleanupTourDemo();
    // Land the user on /brief (their home base, moved from /pipeline
    // 2026-07-20) after the tour finishes. The tour's later stops navigate
    // away (Capacity, Workload, Data Sources), and leaving the user wherever
    // the tour ended post-cleanup means they may be looking at a surface
    // whose demo content just vanished — confusing, and historically the
    // visible symptom of the "white screen on tour finish" bug (when the
    // tour still ended on an admin page).
    if (location.pathname !== '/brief') {
      navigate('/brief');
    }
  };
  const handleGuidedClose = () => {
    // Skip is treated as complete: don't pester them again. They can re-run
    // from the sidebar if they want it.
    handleGuidedComplete();
  };

  // No-data prompt — shown after the tour closes (or on subsequent
  // logins if the user still hasn't loaded any data). Dismissable per
  // session via sessionStorage; reappears next login if they still have
  // zero tickets. User spec 2026-05-23.
  const [noDataOpen, setNoDataOpen] = useState(false);
  const [hasCheckedData, setHasCheckedData] = useState(false);
  // Defensive: if the guided tour ends up opening AFTER the NoDataPrompt
  // is already on screen (race condition between the two effects on the
  // first /brief mount post-invite-accept), close the NoDataPrompt so
  // the user only sees one modal at a time. The post-tour reappearance
  // logic still works because hasCheckedData gets reset on demo-data-
  // deleted, so the prompt re-fires after the tour cleanup.
  useEffect(() => {
    if (guidedTourOpen && noDataOpen) setNoDataOpen(false);
  }, [guidedTourOpen, noDataOpen]);

  useEffect(() => {
    if (!user) return;                    // wait for auth
    if (tourOpen) return;                 // never compete with the carousel
    if (guidedTourOpen) return;           // never compete with the guided tour either
    // ALSO skip when a tour is PENDING but hasn't opened yet — the tour
    // open path is async (status check + seed) so guidedTourOpen can
    // still be false when this effect fires. Without this guard, the
    // NoDataPrompt opens before the tour does and both show at once.
    // tourCompletedAt===null → guided tour will auto-fire on /brief,
    // so defer the no-data prompt until after it completes.
    if (user.tourCompletedAt == null && location.pathname === '/brief') return;
    if (hasCheckedData) return;           // one check per session is enough
    if (sessionStorage.getItem(NO_DATA_DISMISS_KEY) === '1') {
      setHasCheckedData(true);
      return;
    }
    ticketsApi.list({ limit: '1' })
      .then(r => {
        const tickets = Array.isArray(r.data) ? r.data : (r.data?.tickets ?? []);
        if (tickets.length === 0) setNoDataOpen(true);
      })
      .catch(() => { /* if the call fails we silently skip — better to miss the prompt than show it erroneously */ })
      .finally(() => setHasCheckedData(true));
  }, [user, tourOpen, guidedTourOpen, hasCheckedData, location.pathname]);

  const handleNoDataClose = () => {
    sessionStorage.setItem(NO_DATA_DISMISS_KEY, '1');
    setNoDataOpen(false);
  };

  // Re-trigger the first-login experience whenever the workspace is wiped
  // (e.g. demo data deleted). Mirrors the brand-new-org flow: tour first, then
  // NoDataPrompt — so the user lands back at the same starting point a fresh
  // sign-up sees.
  useEffect(() => {
    const onDemoDeleted = (e: Event) => {
      // The same event fires from three places:
      //   - DemoDataBanner / DemoDataCard (user clicked "Delete demo data") —
      //     they EXPECT the first-time experience back, so reset welcome flags.
      //   - cleanupTourDemo (auto-fired when the guided tour ends after
      //     seeding demo for the tour run) — the user just finished the
      //     tour; resetting welcome here re-fires the carousel and was the
      //     root cause of the "white screen on tour finish" bug.
      // Differentiate via CustomEvent.detail.fromTour.
      const fromTour = (e as CustomEvent<{ fromTour?: boolean }>).detail?.fromTour === true;
      sessionStorage.removeItem(NO_DATA_DISMISS_KEY);
      setHasCheckedData(false);
      if (fromTour) return;
      // Server-side resetWelcome also nulls tourCompletedAt, so mirror both
      // flags locally — otherwise the guided tour would skip on the next
      // render before the user payload refreshes.
      updateUser({ welcomeSeen: false, tourCompletedAt: null });
      authApi.resetWelcome().catch(() => { /* best-effort; local state already flipped */ });
    };
    window.addEventListener('demo-data-deleted', onDemoDeleted);
    return () => window.removeEventListener('demo-data-deleted', onDemoDeleted);
  }, [updateUser]);

  return (
    <PlanContext.Provider value={planState}>
      <CommandCenterEasterEgg />
      <div className="flex h-screen overflow-hidden bg-surface">
        <Sidebar />
        <div className="flex flex-col flex-1 overflow-hidden">
          {isStaging && (
            <div className="bg-yellow-400 text-yellow-900 text-center text-xs font-bold py-1.5 tracking-widest uppercase flex-shrink-0">
              ⚠ Staging Environment — Changes here do not affect production
            </div>
          )}
          <Header />
          <DemoDataBanner />
          <main className="flex-1 overflow-auto p-6">
            {/* A page render crash shows a recoverable message here instead of a
                blank white content area; nav stays usable. Keyed on path so
                navigating away clears the error. RouteSuspense keeps the shell
                (sidebar/header) painted while a code-split page chunk loads. */}
            <ErrorBoundary resetKey={location.pathname}>
              <RouteSuspense>
                {/* Clean-slate chrome (approved 2026-07-18): the global
                    controls live in the Sidebar (SidebarAccountZone +
                    AddDataMenu's sidebar variant) under the v2 flag; v1
                    keeps them in Header's green band. Layout no longer
                    injects anything into PageChrome's row. */}
                <Outlet />
              </RouteSuspense>
            </ErrorBoundary>
          </main>
        </div>
        <OnboardingTour
          open={tourOpen}
          onClose={() => setTourOpen(false)}
        />
        <GuidedTour
          key={`guided-tour-${guidedTourSession}`}
          open={guidedTourOpen}
          steps={guidedSteps}
          logoUrl={companyLogo}
          onClose={handleGuidedClose}
          onComplete={handleGuidedComplete}
        />
        {/* First-run "what's your role?" capture — after the tour, never during. */}
        <PersonaPrompt suppressed={tourOpen || guidedTourOpen} />
        {/* Document → Stories, reachable everywhere: "+ Add data" menu event +
            app-wide drag-and-drop of .docx/.txt/.md (suppressed on routes with
            native drop zones). */}
        <DocImportModal />
        {/* Idle-timeout countdown ("still there?") — driven by sessionManager. */}
        <SessionIdleModal />
        {/* One-time privacy-policy acceptance for existing users who never signed
            it (new signups accept at account creation). Renders nothing unless
            the server flags user.needsPrivacyAcceptance. */}
        <PrivacyAcceptanceGate />
        {tourSeeding && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
            <div className="bg-card rounded-xl shadow-2xl w-full max-w-md p-6 text-center">
              <div className="text-3xl mb-2">🌱</div>
              <h2 className="font-semibold text-ink text-lg mb-1">
                Setting up sample data…
              </h2>
              <p className="text-sm text-ink-muted mb-4">
                Loading the Dunder Mifflin Platform project so the tour shows real
                content. We'll remove it automatically when the tour ends.
              </p>
              {tourSeedError ? (
                <>
                  <p className="text-sm text-amber-700 mb-4">{tourSeedError}</p>
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={() => { setTourSeeding(false); setTourSeedError(null); openTourImmediate(); }}
                      className="px-4 py-2 text-sm rounded border border-line text-ink-muted hover:bg-wash"
                    >
                      Run tour without sample data
                    </button>
                    <button
                      onClick={() => { setTourSeeding(false); setTourSeedError(null); }}
                      className="px-4 py-2 text-sm rounded bg-brand-primary text-white font-medium hover:bg-brand-primary-dark"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div className="h-2 bg-wash rounded overflow-hidden">
                  <div className="h-full bg-brand-primary w-1/2 animate-pulse" />
                </div>
              )}
            </div>
          </div>
        )}
        <NoDataPrompt open={noDataOpen} onClose={handleNoDataClose} />
      </div>
    </PlanContext.Provider>
  );
}
